/**
 * 注文明細の金額の算出ルール (Issue #736 / #775)。admin の受注画面と driver の配達先詳細で共有する。
 * 表示規約は `docs/05_ui/03_orders.md` の「金額表示の約束」を参照。
 *
 * `null` は「算出できない」を意味する。**画面に何と出すかはここでは決めない**
 * (admin は `—`、driver は `未設定` / `未算出`)。算出規約だけを共通にし、語は呼び出し側が選ぶ
 * (`docs/05_ui/01_overview.md` の「金額表示の共通方針」)。
 * 単価 0 円は 0 として算出する (算出不能にしない)。合計が 0 円・負数になる場合もそのまま返す。
 *
 * **「明細が空」も null になる。** 呼び出し側は「明細が無い」と「単価が未入力」を自分で区別する
 * (admin・driver とも合計欄そのものを明細 0 件では出していない)。
 */
export type AmountItem = { quantity: number; unit_price: number | null };

/**
 * 行の小計。単価未入力の行は算出しない (null)。
 *
 * 行が無い・数量や単価が欠けている場合も null を返し、**画面に `NaN円` を出さない**。
 * 型の上では起きないが、driver は API レスポンスを実行時検証せずに流し (`hooks.ts` の `as`)、
 * admin のフォームは配列が縮んだ直後など行が外れる過程の値を読むため、実行時には起こりうる。
 */
export function lineSubtotal(item: AmountItem | undefined): number | null {
  if (!item || item.unit_price == null) return null;
  const subtotal = item.quantity * item.unit_price;
  return Number.isFinite(subtotal) ? subtotal : null;
}

/**
 * 明細の合計。単価が入っている行が 1 つも無ければ算出しない (null)。
 * 単価未入力の行は 0 円として加算しない (部分和を返す)。
 * フォームではストアの通知ごとに走るので、中間配列を作らず 1 回の走査で済ませる。
 */
export function itemsTotal(items: readonly AmountItem[]): number | null {
  let total = 0;
  let priced = false;
  for (const item of items) {
    const subtotal = lineSubtotal(item);
    if (subtotal === null) continue;
    total += subtotal;
    priced = true;
  }
  return priced ? total : null;
}
